/**
 * Areas insérables dans le workbench d'un atelier.
 */

import {AccelKeyMgr, ACTION, Action} from "/@lib@/commons/actions.js";
import {AreaAsync} from "/@lib@/commons/areas.js";
import {IO} from "/@lib@/commons/io/io.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IView} from "/@lib@/commons/views.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {TemplateResult} from "/@res@/litHtml/lib/template-result.js";
import {render} from "/@lib@/commons/utils/htmlLit.js";
import {MsgLabel} from "/@back@/commons/basis.js";
import {GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";

import type {OSearchesInit, Searches} from "/@back@/wsp/views/searches.js";
import type {OGeneratorTabsInit} from "/@back@/wsp/views/generators.js";
import type {ISpaceTreeCtx, OSpaceTreeInit, SpaceTree} from "/@back@/wsp/views/spaceTree.js";
import type {IShortDescCtx} from "/@lib@/wsp/item.js";
import type {IWspUiEnv, Wsp} from "/@lib@/wsp/wsp.js";
import type {IBkCtx, OBookmarksInit} from "/@back@/wsp/views/bookmarks.js";
import type {OTocInit} from "/@back@/wsp/views/toc.js";
import type {ISrcGridCtx} from "/@back@/wsp/widgets/srcGrid.js";
import type {IInfoBrokerPointer} from "/@lib@/commons/infos.js";
import type {IChainEnv} from "/@lib@/wsp/chain.js";
import type {OActionBtnInit} from "/@back@/commons/widgets/buttons.js";
import type {IWbToolArea} from "/@back@/core/widgets/workbench.js";
import type {JNetItemConf, ONetItemsInit} from "/@back@/wsp/views/netItems.js";
import type {ESrcField, JSrcFields} from "/@lib@/wsp/src.js";
import type {INavigatorFacets, Navigator, ONavigatorInit} from "/@back@/wsp/views/navigator.js";

/**
 * Area construisant la view "Explorer" pour la wspApp.
 */
export class ExplorerArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> {

	/** Listes d'actions associées aux items (Action<ISpaceTreeCtx>). */
	actionsLists: string[];

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction: Action<ISpaceTreeCtx>;

	/** Racourcis claviers à capter sur la sel courante (Action<ISpaceTreeCtx>). */
	accelKeysLists: string[];

	/** Racine de l'arbre ("" par défaut). */
	uriRoot: string;

	/** Si uriRoot!="", affiche l'espace racine ou directement sa liste de fils. */
	showRoot: boolean;

	/** */
	hideItemFolderContent: boolean;

	constructor() {
		super('explorer');
		this._label = "Explorateur";
		this._icon = "/@skin@/wsp/views/spaceTree/treeTabf.svg";
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/spaceTree.js"));
	}

	setActionsLists(actionsLists: string[]): this {
		this.actionsLists = actionsLists;
		return this;
	}

	setAccelKeysLists(accelKeysLists: string[]): this {
		this.accelKeysLists = accelKeysLists;
		return this;
	}

	setDefaultAction(defaultAction: Action<ISpaceTreeCtx>): this {
		this.defaultAction = defaultAction;
		return this;
	}

	setHideItemFolderContent(hide: boolean): this {
		this.hideItemFolderContent = hide;
		return this;
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const reg = ctx.reg;
		const infoBroker = reg.env.infoBroker;
		const mod: typeof import("/@back@/wsp/views/spaceTree.js") = this._modules[0];
		const init: OSpaceTreeInit = {
			area: this,
			areaContext: ctx,
			reg: reg,
			uriRoot: this.uriRoot,
			showRoot: this.showRoot,
			hideItemFolderContent: this.hideItemFolderContent,
			secondaryCols: reg.getList("columns:wspExplorer:secondaryCols"),
			lineDrawers: reg.getList("lineDrawers:wspExplorer"),
			showTitle: reg.getUserData("wsp.explorer.showTitle", true),
			itemHandlingReact: infoBroker,
			actions: this.actionsLists ? reg.mergeLists(...this.actionsLists) : null,
			defaultAction: this.defaultAction !== undefined ? this.defaultAction : (infoBroker ? new this.srcActions.FocusItem() : null),
			accelKeyMgr: this.accelKeysLists ? new AccelKeyMgr<IShortDescCtx>().initFromMapActions(reg.mergeListsAsMap(...this.accelKeysLists)) : null,
			emptyTree: (spaceTree) => <div style="font-style:normal">
				<c-msg label="Aucun contenu" level="info"/>
				<c-action î={{
					action: new mod.SpaceTreeCreateItem(),
					actionContext: spaceTree,
					uiContext: "dialog",
					icon: "/@skin@/wsp/actions/createItem.svg"
				} as OActionBtnInit<SpaceTree>}/>
				<c-action î={{
					action: new mod.SpaceTreeCreateSpace(),
					actionContext: spaceTree,
					uiContext: "dialog",
					icon: "/@skin@/wsp/actions/createSpace.svg"
				} as OActionBtnInit<SpaceTree>}/>
			</div>
		};


		return <wsp-space-tree î={init}/>;
	}

	srcActions: typeof import("/@back@/wsp/actions/srcActions.js");

	async loadLibs(ctx: IRegPointer<IWspUiEnv>): Promise<void> {
		this.srcActions = await import("/@back@/wsp/actions/srcActions.js");
		return super.loadLibs(ctx);
	}

}


/**
 * Area construisant la view "Générateurs" pour la wspApp.
 */
export class GeneratorTabsArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> implements IWbToolArea<IRegPointer<IWspUiEnv>, IView> {

	//get forbidInLayout(): 'h' | 'v' {return 'v'}

	constructor() {
		super('generators');
		this._label = "Publications";
		this._icon = "/@skin@/wsp/views/generators/generators.svg";
		this._lastDatasKey = 'gen';
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/generators.js"));
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		return <wsp-generator-tabs î={this.initLastDatas(ctx, lastDatas, {
			area: this,
			areaContext: ctx,
			reg: ctx.reg
		} as OGeneratorTabsInit)}/>;
	}
}


/**
 * Area construisant la view "Recherches" pour la wspApp.
 */
export class SearchesArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> implements IWbToolArea<IRegPointer<IWspUiEnv>, IView> {

	get forbidInLayout(): 'h' | 'v' {return 'v'}

	/** Listes d'actions associées aux items (Action<ISrcGridCtx>). */
	actionsLists: string[];

	/** Action exécutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction: Action<ISrcGridCtx>;

	/** Raccourcis claviers à capter sur la sel courante (Action<ISrcGridCtx>). */
	accelKeysLists: string[];

	constructor(id: string, public kind: 'item' | 'task') {
		super(id);
		this._label = kind === 'item' ? "Items" : "Tâches";
		this._description = kind === 'item' ? "Recherche d'items" : "Recherche de tâches";
		this._icon = kind === 'item' ? "/@skin@/wsp/views/searches/searches.svg" : "/@skin@/wsp/views/searches/searches-tasks.svg";
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/searches.js"));
	}

	setActionsLists(actionsLists: string[]): this {
		this.actionsLists = actionsLists;
		return this;
	}

	setAccelKeysLists(accelKeysLists: string[]): this {
		this.accelKeysLists = accelKeysLists;
		return this;
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const infoBroker = ctx.reg.env.infoBroker;

		// Exemple de déclaration de requetes d'items dans la bibliothèque
		/**
		REG.reg.addToList("requests:item", "test1", 1, {
			title : "Requete prédéfinie 1",
			whereCrit: <where><exp type="Items" exclude="/~annot/;/~air/;/~ext/" uiCrit="wsp-itemcrit-fullwsp"/><and><exp type="FullText" textSearch="11" options=";c;" uiCrit="wsp-itemcrit-fulltext"/></and></where>
		} as ISearchPersist);
		REG.reg.addToList("requests:item", "test2", 1, {
			title : "Requete prédéfinie 2",
			description : "Ma description !!!",
			whereCrit: <where><exp type="Items" exclude="/~annot/;/~air/;/~ext/" uiCrit="wsp-itemcrit-fullwsp"/><and><exp type="FullText" textSearch="22" options=";c;" uiCrit="wsp-itemcrit-fulltext"/></and></where>
		} as ISearchPersist);

		REG.reg.addToList("requests:task", "test2", 1, {
			title : "Requete prédéfinie 2de taches 1",
			description : "Ma description !!!",
			whereCrit: <where><exp type="TasksAll" actStages="pending completed forthcoming" uiCrit="wsp-taskcrit-fullwsp"/><and><exp type="ItemSgnRegexp" regexpSgn="(@stk_postit|@stk_commande)\b.*" uiCrit="wsp-taskcrit-type" uiItModels="stk_postit|stk_commande"/></and></where>
		} as ISearchPersist);
		 **/


		// barActionsInit
		if (this.kind === 'item') {
			ctx.reg.addToList("actions:wspApp:searches:bar:end:item", "actionsSearchPersistLists", 1, this.searchesActions.TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:item", "requests:wspApp:item", "requests:searches:item"));
			ctx.reg.addToList("actions:wspApp:searches:bar:end:item", "actionOpenSearchItems", 1, new this.srcActions.OpenSearchItems('item').setGroup('noctx'));
		} else {
			ctx.reg.addToList("actions:wspApp:searches:bar:end:task", "actionsSearchPersistLists", 1, this.searchesActions.TplRequestsMenu.makeFromSearchPersistLists(ctx, "requests:task", "requests:wspApp:task", "requests:searches:task"));
			ctx.reg.addToList("actions:wspApp:searches:bar:end:task", "actionOpenSearchItems", 1, new this.srcActions.OpenSearchItems('task').setGroup('noctx'));
		}

		return <wsp-searches î={{
			area: this,
			areaContext: ctx,
			reg: ctx.reg,
			kind: this.kind,
			privateUri: this.kind === 'item' ? '/itemSearches.json' : '/taskSearches.json',
			criterions: ctx.reg.getListAsMap(this.kind === 'item' ? "wsp.crit.items" : "wsp.crit.tasks"),
			barActionsInit: {
				actions: ctx.reg.getList(this.kind === 'item' ? "actions:wspApp:searches:bar:end:item" : "actions:wspApp:searches:bar:end:task") as Action<Searches>[],
				groupOrder: "* lib noctx",
			},
			results: {
				srcGrid: {
					columnDefs: ctx.reg.getList(this.kind === 'item' ? "columns:wspApp:searches:item" : "columns:wspApp:searches:task"),
					itemHandlingReact: infoBroker,
					actions: this.actionsLists ? ctx.reg.mergeLists(...this.actionsLists) : null,
					defaultAction: this.defaultAction !== undefined ? this.defaultAction : (infoBroker ? this.kind === 'item' ? new this.searches.FocusItemSetWedSearch() : new this.taskActions.FocusTask() : null),
					accelKeyMgr: this.accelKeysLists ? new AccelKeyMgr<IShortDescCtx>().initFromMapActions(ctx.reg.mergeListsAsMap(...this.accelKeysLists)) : null,
					draggable: true,
					lineDrawer: {
						redrawLine: (row: GridDataRowJson<JSrcFields>, line) => {
							if (this.kind === 'task') {
								const dt = row.rowDatas.tkDeadline;
								line.classList.toggle("late", dt > 0 && dt < Date.now());
								line.classList.toggle("completed", row.rowDatas.actStage === "completed");
							}
						}
					},
					skinOver: "wsp-search-result/grid",
					onWspUriChangeScope: "longDesc",
				},
				barActions: ACTION.injectSepByGroup(ctx.reg.mergeLists(this.kind === 'item' ? "actions:wspApp:searches:item" : "actions:wspApp:searches:task", "actions:wspApp:searches"), '* burger', ctx),
				msgs: this.kind === 'item' ? undefined : {
					resultsMore: max => `Plus de ${max} tâches trouvées`,
					resultsEmpty: 'Aucune tâche trouvée',
					resultsOne: '1 tâche trouvée',
					resultsCount: count => `${count} tâches trouvées`,
				},
			}
		} as OSearchesInit}/>;
	}

	srcActions: typeof import("/@back@/wsp/actions/srcActions.js");
	taskActions: typeof import("/@back@/wsp/actions/taskActions.js");
	searches: typeof import("/@back@/wsp/views/searches.js");
	searchesActions: typeof import("/@back@/wsp/actions/searchesActions.js");


	async loadLibs(ctx: IRegPointer<IWspUiEnv>): Promise<void> {
		this.searches = await import("/@back@/wsp/views/searches.js");
		this.srcActions = await import("/@back@/wsp/actions/srcActions.js");
		this.searchesActions = await import("/@back@/wsp/actions/searchesActions.js");
		if (this.kind === 'task') this.taskActions = await import("/@back@/wsp/actions/taskActions.js");
		return super.loadLibs(ctx);
	}
}

REG.reg.registerSkin("wsp-search-result/grid", 1, /* language=CSS */ `

	.late {
		color: var(--error-color);
	}

	.completed {
		text-decoration-line: line-through;
	}
`);

class AreaWithActions<C> extends AreaAsync<IRegPointer<IWspUiEnv>, IView> {

	/** Listes d'actions associées aux items (Action<IBkCtx>). */
	actionsLists: string[];

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction: Action<C>;

	/** Racourcis claviers à capter sur la sel courante (Action<IBkCtx>). */
	accelKeysLists: string[];


	setActionsLists(actionsLists: string[]): this {
		this.actionsLists = actionsLists;
		return this;
	}

	setAccelKeysLists(accelKeysLists: string[]): this {
		this.accelKeysLists = accelKeysLists;
		return this;
	}

	setDefaultAction(defaultAction: Action<C>): this {
		this.defaultAction = defaultAction;
		return this;
	}
}


/**
 * Area construisant la view "Bookmarks" pour la wspApp.
 */
export class BookmarksArea extends AreaWithActions<IBkCtx> {

	constructor() {
		super('bookmarks');
		this._label = "Favoris";
		this._icon = "/@skin@/wsp/views/bookmarks/bookmarks.svg";
		this._lastDatasKey = 'bk';
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/bookmarks.js"));
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const reg = ctx.reg;
		const infoBroker = ctx.reg.env.infoBroker;
		return <wsp-bookmarks î={this.initLastDatas(ctx, lastDatas, {
			reg: reg,
			area: this,
			areaContext: ctx,
			itemHandlingReact: infoBroker,
			secondaryCols: reg.getSvc("wsp-bookmarks/secondaryCols"),
			actions: this.actionsLists ? ctx.reg.mergeLists(...this.actionsLists) : null,
			defaultAction: this.defaultAction !== undefined ? this.defaultAction : (infoBroker ? new this.srcActions.FocusItemOrSpace() : null),
			accelKeyMgr: this.accelKeysLists ? new AccelKeyMgr<IShortDescCtx>().initFromMapActions(ctx.reg.mergeListsAsMap(...this.accelKeysLists)) : null
		} as OBookmarksInit)}/>;
	}

	srcActions: typeof import("/@back@/wsp/actions/srcActions.js");

	async loadLibs(ctx: IRegPointer<IWspUiEnv>): Promise<void> {
		this.srcActions = await import("/@back@/wsp/actions/srcActions.js");
		return super.loadLibs(ctx);
	}
}


/**
 * Area construisant la view "Toc" pour la wspApp.
 */
export class TocArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> {

	constructor(id?: string) {
		super(id || 'toc');
		this._label = "Plan";
		this._icon = "/@skin@/wsp/views/toc/tocTabf.svg";
		this._lastDatasKey = 'toc';
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/toc.js"));
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const infoBroker = ctx.reg.env.infoBroker;
		return <wsp-toc î={this.initLastDatas(ctx, lastDatas, {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			infoBroker: infoBroker
		} as OTocInit)}/>;
	}
}

/**
 * Area construisant la view "Réseau des items" pour la wspApp.
 */
export class NetItemsArea extends AreaWithActions<IShortDescCtx> {

	/** Code de la liste des confs de type JNetItemConf */
	confsList?: string[];

	setConfsList(list: string[] | null): this {
		this.confsList = list;
		return this;
	}

	constructor(id?: string) {
		super(id || 'netItems');
		this._label = "Réseau";
		this._description = "Réseau d'items";
		this._icon = "/@skin@/wsp/views/netItems/netItemsTabf.svg";
		this._lastDatasKey = 'netItems';
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/netItems.js"));
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const infoBroker = ctx.reg.env.infoBroker;

		return <wsp-net-items î={this.initLastDatas(ctx, lastDatas, {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			confs: this.confsList ? ctx.reg.mergeLists<JNetItemConf>(...this.confsList) : null,
			infoBroker: infoBroker,
			itemHandlingReact: infoBroker,
			secondaryCols: ctx.reg.getList("columns:netItems:secondaryCols"),
			actions: this.actionsLists ? ctx.reg.mergeLists(...this.actionsLists) : null,
			defaultAction: this.defaultAction !== undefined ? this.defaultAction : (infoBroker ? new this.srcActions.FocusItemOrSpace() : null),
			accelKeyMgr: this.accelKeysLists ? new AccelKeyMgr<IShortDescCtx>().initFromMapActions(ctx.reg.mergeListsAsMap(...this.accelKeysLists)) : null
		} as ONetItemsInit)}/>;
	}

	srcActions: typeof import("/@back@/wsp/actions/srcActions.js");

	async loadLibs(ctx: IRegPointer<IWspUiEnv>): Promise<void> {
		this.srcActions = await import("/@back@/wsp/actions/srcActions.js");
		return super.loadLibs(ctx);
	}
}


/**
 * Area construisant la view "Tasks" pour la wspApp.
 */
export class TasksArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> {

	constructor() {
		super('myTasks');
		this._label = "Mes tâches";
		this._icon = "/@skin@/wsp/views/tasks/tasksTabf.svg";
		this._lastDatasKey = 'myTasks';
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/tasks.js"));
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const infoBroker = ctx.reg.env.infoBroker;
		return <wsp-tasks î={this.initLastDatas(ctx, lastDatas, {
			reg: ctx.reg,
			area: this,
			areaContext: ctx,
			infoBroker: infoBroker
		} as OTocInit)}/>;
	}
}


/**
 * Area construisant une vue offrant un contexte d'atelier défini chargé
 */
export class ForeignWspArea extends AreaAsync<IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>, IView> {
	constructor(id: string, public wspCode: string, public template: (wsp: Wsp, ctx: IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>) => Promise<TemplateResult>) {
		super(id);
		this._lastDatasKey = id;
	}

	needAsync(ctx: IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>): boolean {
		return true;
	}

	async loadBody(ctx: IRegPointer<IChainEnv & IInfoBrokerPointer & IUiEnv>, lastDatas?: JLastDatas): Promise<IView> {
		const infoBroker = ctx.reg.env.infoBroker;
		const wsp = ctx.reg.env.universe.wspServer.wspsLive.findWsp(this.wspCode, true).listenChanges();
		const msg = <MsgLabel î={{label: 'Chargement en cours...'}}/>;
		const root = <div style="display:contents">
			{msg}
		</div>;
		try {
			wsp.waitForAvailable().then(async (wsp) => {
				render(await this.template(wsp, ctx), root, {});
			}).catch((e) => {
				console.log(e);
				return msg.setCustomMsg("L'atelier demandé n'existe pas ou est inaccessible.", "error");
			});
			return root;
		} catch (e) {
			console.log(e);
			return msg.setCustomMsg("L'atelier demandé n'existe pas ou est inaccessible.", "error");
		}
	}
}


/**
 * Area construisant une view "Navigator" pour la wspApp.
 */
export class NavigatorArea extends AreaAsync<IRegPointer<IWspUiEnv>, IView> {

	/** Listes d'actions associées aux items (Action<IShortDescCtx>). */
	actionsLists: string[];

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction: Action<IShortDescCtx>;

	/** Racourcis claviers à capter sur la sel courante (Action<IShortDescCtx>). */
	accelKeysLists: string[];

	/**
	 * Construction du gestionnaire des facettes de ce navigateur et de la construction de la requête au serveur.
	 */
	buildFacets?: (nav: Navigator, fields: ESrcField[]) => Promise<INavigatorFacets>;

	/** Contenu de la zone principale lorsque la liste est vide. */
	emptyTree?: (navigator: Navigator) => Node

	/**
	 * Facette standard définie dans "/@back@/wsp/views/navigator".
	 * La valeur correspond au noù du symbole exporté.
	 */
	buildFacetsStandard?: keyof typeof import("/@back@/wsp/views/navigator.js");

	constructor(id: string) {
		super(id);
		this.requireLib(IO.asEndPoint("/@back@/wsp/views/navigator.js"));
	}

	setActionsLists(actionsLists: string[]): this {
		this.actionsLists = actionsLists;
		return this;
	}

	setAccelKeysLists(accelKeysLists: string[]): this {
		this.accelKeysLists = accelKeysLists;
		return this;
	}

	setDefaultAction(defaultAction: Action<IShortDescCtx>): this {
		this.defaultAction = defaultAction;
		return this;
	}

	setEmptyTree(emptyTree:(navigator: Navigator) => Node): this {
		this.emptyTree = emptyTree;
		return this;
	}

	setBuildFacets(buildFacets: (nav: Navigator, fields: ESrcField[]) => Promise<INavigatorFacets>): this {
		this.buildFacets = buildFacets;
		return this;
	}

	setBuildFacetsStandard(facets: keyof typeof import("/@back@/wsp/views/navigator.js")): this {
		this.buildFacetsStandard = facets;
		return this;
	}

	buildBody(ctx: IRegPointer<IWspUiEnv>, lastDatas?: JLastDatas): IView {
		const reg = ctx.reg;
		const infoBroker = reg.env.infoBroker;
		const mod: typeof import("/@back@/wsp/views/navigator.js") = this._modules[0];
		const init: ONavigatorInit = {
			area: this,
			areaContext: ctx,
			reg: reg,
			emptyTree: this.emptyTree,
			buildFacets: this.buildFacets || mod[this.buildFacetsStandard] as any,
			secondaryCols: reg.getList("columns:wspExplorer:secondaryCols"), //Même key que l'explorateur ?
			itemHandlingReact: infoBroker,
			actions: this.actionsLists ? reg.mergeLists(...this.actionsLists) : null,
			defaultAction: this.defaultAction !== undefined ? this.defaultAction : (infoBroker ? new this.srcActions.FocusItem() : null),
			accelKeyMgr: this.accelKeysLists ? new AccelKeyMgr<IShortDescCtx>().initFromMapActions(reg.mergeListsAsMap(...this.accelKeysLists)) : null,
			lastDatas: lastDatas
		};

		return <wsp-navigator î={init}/>;
	}

	srcActions: typeof import("/@back@/wsp/actions/srcActions.js");

	async loadLibs(ctx: IRegPointer<IWspUiEnv>): Promise<void> {
		this.srcActions = await import("/@back@/wsp/actions/srcActions.js");
		return super.loadLibs(ctx);
	}

}


